import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo } from "../../lib/seo.js";
import { Section } from "../../components/Section.js";

interface ProviderRow {
  name: string;
  pricing: string;
  distribution: string;
  pf: string;
  certification: string;
  integration: string;
}

const PROVIDERS: ProviderRow[] = [
  {
    name: "Vault Breaker",
    pricing: "10% rev-share, $0 setup",
    distribution: "Direct REST + WS",
    pf: "Open-source @vb/engine, per-round verifiable",
    certification: "GLI-19 within 60 days of first pilot (no cert today)",
    integration: "~1 week, three live games",
  },
  {
    name: "Spribe (Aviator)",
    pricing: "~$50k setup + 5-15% GGR",
    distribution: "Direct + most aggregators",
    pf: "In-house PF scheme, audited",
    certification: "GLI, BMM, iTechLabs",
    integration: "4-8 weeks, single hero title",
  },
  {
    name: "Smartsoft (JetX)",
    pricing: "Rev-share, undisclosed setup",
    distribution: "Aggregator-led",
    pf: "Closed-source PF, audited",
    certification: "GLI, BMM",
    integration: "3-6 weeks via aggregator",
  },
  {
    name: "Turbo Games",
    pricing: "Rev-share, low setup",
    distribution: "Aggregator-led",
    pf: "Closed-source PF",
    certification: "GLI on select titles",
    integration: "2-4 weeks via aggregator",
  },
  {
    name: "Hacksaw",
    pricing: "Rev-share, mid setup",
    distribution: "Aggregator-led",
    pf: "Closed-source RNG, no PF on most titles",
    certification: "GLI, MGA",
    integration: "3-5 weeks via aggregator",
  },
  {
    name: "BGaming",
    pricing: "Rev-share + low setup",
    distribution: "Direct + aggregators",
    pf: "Per-round PF on most titles",
    certification: "iTechLabs, MGA, regulated markets",
    integration: "2-4 weeks direct, 1 week via aggregator",
  },
];

export function SpribeAlternative() {
  return (
    <>
      <Seo
        title="Spribe Aviator alternatives in 2026 — what to evaluate · Vault Breaker Studios"
        description="Honest comparison of five Aviator-class providers — Vault Breaker, Smartsoft JetX, Turbo Games, Hacksaw, BGaming — with a decision tree for direct vs aggregator-locked stacks."
        path="/blog/spribe-alternative"
      />

      <Section style={{ paddingBottom: 24 }}>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          IGAMING STACK · 6 MIN READ
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(36px, 5vw, 56px)",
            lineHeight: 1.05,
            margin: 0,
            color: "var(--fg-1)",
            letterSpacing: "0.01em",
          }}
        >
          Spribe Aviator alternatives in 2026 — what to evaluate.
        </h1>
        <p
          style={{
            fontSize: 16,
            color: "var(--fg-3)",
            marginTop: 16,
            fontFamily: "var(--font-mono)",
          }}
        >
          2026-05-05
        </p>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 820 }}>
        <Prose>
          <p>
            Aviator owns the crash category. It's also the most aggressively priced casino vertical
            of the last five years — Spribe holds the IP, the marketing budget, and the exclusivity
            clauses, and they price accordingly. Most operators we talk to are looking for a second
            crash title because Aviator's margins don't add up below a certain MAU threshold, or
            because their preferred aggregator doesn't carry it, or because the certification
            timeline doesn't match their launch window.
          </p>
          <p>
            This post is the honest version of the comparison: who else builds Aviator-class titles,
            what their commercial terms look like in 2026, and a decision tree for choosing.
          </p>

          <H2>(a) Why operators look for an alternative</H2>
          <p>The three reasons we hear, in roughly the frequency we hear them:</p>
          <ul>
            <li>
              <strong>Price.</strong> Spribe Aviator is sold at tier-1 pricing — $50k setup,
              5-15% of GGR depending on tier, monthly minimums in the $5-12k range for the
              enterprise SLA. Mid-tier operators do the math at 50k MAU and decide it doesn't
              clear.
            </li>
            <li>
              <strong>Exclusivity.</strong> Spribe has historically restricted multi-Aviator
              setups inside a single operator's catalogue. If you also want JetX or any other
              crash title for product variety, you may need separate contracts and waivers.
            </li>
            <li>
              <strong>Certification timeline.</strong> Tier-1 crash titles ship with GLI / BMM /
              iTechLabs paperwork, but extending coverage to a new jurisdiction (Anjouan,
              Tobique, fresh Curaçao master license) is a 3-6 month process. Operators on a
              tighter launch window go shopping.
            </li>
          </ul>

          <H2>(b) The five candidates — comparison</H2>
          <p>
            Numbers below are typical 2026 deal terms surfaced from operator referrals — your
            mileage will vary by region, traffic, and negotiation. Vault Breaker's row is the
            honest version, gaps included.
          </p>
          <Card style={{ padding: 0, overflow: "hidden", marginTop: 12 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(140px, 1fr) repeat(5, minmax(160px, 1.2fr))",
                padding: "12px 16px",
                background: "var(--bg-2)",
                borderBottom: "1px solid var(--line)",
                fontSize: 10,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "var(--fg-3)",
              }}
            >
              <span>Provider</span>
              <span>Pricing</span>
              <span>Distribution</span>
              <span>PF model</span>
              <span>Certification</span>
              <span>Time-to-integrate</span>
            </div>
            {PROVIDERS.map((p, i) => (
              <div
                key={p.name}
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "minmax(140px, 1fr) repeat(5, minmax(160px, 1.2fr))",
                  padding: "14px 16px",
                  borderTop: i === 0 ? "none" : "1px solid var(--line)",
                  fontSize: 12,
                  color: "var(--fg-2)",
                  lineHeight: 1.5,
                  background: p.name === "Vault Breaker" ? "rgba(0,229,255,0.04)" : "transparent",
                }}
              >
                <span style={{ color: "var(--fg-1)", fontWeight: 600 }}>{p.name}</span>
                <span>{p.pricing}</span>
                <span>{p.distribution}</span>
                <span>{p.pf}</span>
                <span>{p.certification}</span>
                <span>{p.integration}</span>
              </div>
            ))}
          </Card>
          <p style={{ marginTop: 16 }}>
            The Vault Breaker honesty disclosures: we're a single engineer, we have three live
            games (not a catalogue of forty), and we don't have GLI yet. We compensate with the
            things that don't require capital — open-source engine, per-round verifiability, $0
            setup, weekly USDT/USDC settlement, and an integration window measured in days.
          </p>

          <H2>(c) The decision tree — direct or aggregator?</H2>
          <p>
            The most important fork in this decision is whether your stack already routes
            through an aggregator (SoftSwiss, Relax, EveryMatrix, BetConstruct). It changes the
            economics significantly:
          </p>
          <ul>
            <li>
              <strong>If you're aggregator-locked</strong> — you'll pay another 5-15% on top of
              the studio's rev-share to the aggregator, but integration is days not weeks. In
              this scenario Turbo Games, BGaming, and Smartsoft all surface immediately on most
              aggregator catalogues. Vault Breaker doesn't yet — the SoftSwiss and Relax
              adapters are on our Q4 2026 roadmap.
            </li>
            <li>
              <strong>If you do direct integrations</strong> — you'll save the aggregator margin
              and own the contract. Direct contracts with Spribe and Smartsoft are slower
              (3-6 months of legal back-and-forth); BGaming and Vault Breaker are days. If
              you've already built one direct adapter, the second is half the work.
            </li>
            <li>
              <strong>If you're starting from zero</strong> — pick aggregator for time-to-market,
              direct for unit economics. The crossover happens around $100k monthly GGR per
              game; below that, the aggregator's speed is worth the margin; above that, you're
              paying real money to outsource a one-week integration.
            </li>
          </ul>

          <H2>(d) Concrete next steps to evaluate Vault Breaker</H2>
          <p>The fastest evaluation path, in order:</p>
          <ol>
            <li>
              Open a sandbox at <code className="mono">/sandbox</code>. POST to{" "}
              <code className="mono">/public/sandbox/operator</code> and you get a 14-day API
              key with play-money settlement, the same six-endpoint contract a paying operator
              gets.
            </li>
            <li>
              Run the integration walkthrough at <code className="mono">/integration</code>.
              Five-line bash recipe. Most engineers wire the adapter end-to-end in an
              afternoon.
            </li>
            <li>
              Pull a round's seeds via{" "}
              <code className="mono">GET /rgs/round/:id/seeds</code> and verify the bust
              multiplier in your browser console using{" "}
              <code className="mono">@vb/engine</code>. This is the part competitors can't
              demonstrate — closed-source PF schemes never let you verify outside their UI.
            </li>
            <li>
              If the integration shape works, send us your projected GGR via{" "}
              <code className="mono">/contact</code> and we'll counter-quote against whatever
              you're holding from a tier-1.
            </li>
          </ol>
          <p>
            Vault Breaker is not the right answer for every operator. If you need a
            forty-game catalogue with regulator paperwork in twelve jurisdictions on day one,
            BGaming is the more mature choice. If you want a single hero title with the
            highest brand recognition in the category, Spribe Aviator is still the answer. If
            you want a per-round-verifiable crash game with rev-share economics that work
            below $150k monthly GGR — and you can integrate in a week — that's where we win.
          </p>
        </Prose>
      </Section>

      <Section style={{ maxWidth: 820 }}>
        <Card style={{ padding: 22, display: "grid", gap: 10 }}>
          <h3
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: 18,
              color: "var(--fg-1)",
              textTransform: "uppercase",
              letterSpacing: "0.01em",
            }}
          >
            14-day sandbox, no commitment
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.6 }}>
            Free 14-day sandbox at <code className="mono">/sandbox</code>. No commitment, no
            contract, no aggregator middleman. Wire the adapter against play-money settlement
            and decide before you talk to sales.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Link to="/sandbox" style={{ textDecoration: "none" }}>
              <Button variant="gold">OPEN A SANDBOX</Button>
            </Link>
            <Link to="/integration" style={{ textDecoration: "none" }}>
              <Button>INTEGRATION DOCS →</Button>
            </Link>
            <Link to="/blog" style={{ textDecoration: "none" }}>
              <Button>← FIELD NOTES</Button>
            </Link>
          </div>
        </Card>
      </Section>
    </>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        marginTop: 36,
        marginBottom: 8,
        fontFamily: "var(--font-display)",
        fontSize: 22,
        color: "var(--fg-1)",
        letterSpacing: "0.01em",
      }}
    >
      {children}
    </h2>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 15,
        color: "var(--fg-2)",
        lineHeight: 1.75,
      }}
    >
      {children}
    </div>
  );
}
